import { useEffect, useRef, useCallback, useState } from "react";
import { useAutoSaveContext } from "@/hooks/useAutoSaveContext";

/**
 * Auto-save hook: triggers save after a debounce delay (default 3s)
 * when auto-save is globally enabled.
 *
 * Returns:
 * - triggerAutoSave: call this on every change / blur to schedule a save
 * - cancelAutoSave: cancel any pending auto-save (e.g. on manual save)
 * - isAutoSaving: true while auto-save is in progress
 */
export function useAutoSave(
  saveFn: () => Promise<void>,
  { delay = 3000 }: { delay?: number } = {},
) {
  const { autoSaveEnabled } = useAutoSaveContext();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  const cancelAutoSave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const triggerAutoSave = useCallback(() => {
    if (!autoSaveEnabled) return;
    cancelAutoSave();
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      setIsAutoSaving(true);
      try {
        await saveFnRef.current();
      } catch {
        // save errors are handled by the save function itself
      } finally {
        setIsAutoSaving(false);
      }
    }, delay);
  }, [autoSaveEnabled, delay, cancelAutoSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cancelAutoSave();
  }, [cancelAutoSave]);

  return { triggerAutoSave, cancelAutoSave, isAutoSaving };
}
